import { DateTime } from 'luxon';

/**
 * Reads a date that is either a Date or a string in ISO format.
 *
 * Dates are kept in ISO format both in state and on the wire, so a string in any other format is a mistake
 * in the calling code. It is rejected rather than guessed at, so that the mistake shows up in the tests of
 * whatever produced it instead of being quietly absorbed here.
 * @param inputDate The date to read
 * @returns A luxon DateTime, invalid when the input was not a date or not in ISO format
 */
function readDate(inputDate: string | Date): DateTime {
  return inputDate instanceof Date ? DateTime.fromJSDate(inputDate) : DateTime.fromISO(inputDate);
}

/**
 * Returns a date formatted as YYYY-MM-DD
 * @param inputDate The date to format, as a Date or an ISO string
 * @returns The formatted date, or null when the input was not a date in ISO format
 */
export function formatDate(inputDate: string | Date | null | undefined): string | null {
  if (!inputDate) {
    return null;
  }

  const date = readDate(inputDate);

  return date.isValid ? date.toFormat('yyyy-MM-dd') : null;
}

/**
 * Returns the calendar date of a Date as YYYY-MM-DD. Unlike toISOString this keeps the day that was picked
 * instead of moving it to the day before or after in time zones that are not UTC.
 * @param date The date to convert
 * @returns The date as a YYYY-MM-DD string, or null when the date is not a valid date
 */
export function toIsoDateString(date: Date | null | undefined): string | null {
  return date ? DateTime.fromJSDate(date).toISODate() : null;
}

/**
 * Reads a stored date back into a Date. A YYYY-MM-DD string is read as a local date, where
 * new Date('2026-09-07') would read it as midnight UTC and land on the day before west of UTC.
 * @param inputDate The date to read, as a Date or an ISO string
 * @returns The date, or null when the input was not a date in ISO format
 */
export function parseDate(inputDate: string | Date | null | undefined): Date | null {
  if (!inputDate) {
    return null;
  }

  const date = readDate(inputDate);

  return date.isValid ? date.toJSDate() : null;
}

/**
 * Checks if a string is in ISO date format (YYYY-MM-DDTHH:mm:ss.sssZ).
 * Not fully compliant with all ISO 8601 variations, but checks for the most common format,
 * and allows for non-valid dates (e.g., "2024-02-30T12:00:00.000Z" will return true).
 * @param str The string to check
 * @returns True if the string is in ISO date format, false otherwise
 */
export function isIsoDateString(str: string): boolean {
  return DateTime.fromISO(str).isValid && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(str);
}
